<template>
  <div>
    <button
      class="edit-passenger__button"
      @click="openModal"
    >
      <svg>
        <use :xlink:href="`#${person}`" />
      </svg>
      <div class="edit-passenger__button-text">
        <span>{{ $t('common.passenger.title') }}</span>
        {{ passengerText }}
      </div>
    </button>
    <div
      v-if="isModalOpen"
      class="edit-passenger__modal-overlay"
      @click="closeModal"
    >
      <div
        class="edit-passenger__modal-content"
        @click.stop
      >
        <PassengerPicker :initialPassengers="passengers" />
        <div class="edit-passenger__modal-actions">
          <button
            class="button button--medium button--secondary"
            @click="cancelChanges"
          >
            {{ $t('common.actions.cancel') }}
          </button>
          <button
            @click="closeModal"
            class="button button--medium button--primary"
          >
            {{ $t('common.actions.save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed } from 'vue'
  import { useJourneyStore } from '@/stores/journeyStore'
  import PassengerPicker from './PassengerPicker.vue'
  import person from '@/assets/icons/person.svg'
  import { useI18n } from 'vue-i18n'
  import { formatPrice } from '@/utils/formatPrice'

  export default defineComponent({
    name: 'EditPassenger',
    components: {
      PassengerPicker,
    },
    setup() {
      const journeyStore = useJourneyStore()
      const isModalOpen = ref(false)
      const initialPassengers = ref([])
      const i18n = useI18n()

      const passengers = computed(() => journeyStore.passengers)

      const passengerText = computed(() => {
        const typeMapping: Record<string, string> = {
          'ADT': 'adult',          
          'CHD': 'child',         
          'COM': 'companion',     
          'CON': 'conscript',     
          'SEN': 'senior',        
          'STU': 'student',       
          'YOU': 'child',         
          'INF': 'infant'          
        }

        const passengerCounts = passengers.value.reduce((acc, passenger) => {
          if (!passenger.type) return acc
          
          const apiType = passenger.type
          const translationType = typeMapping[apiType] || apiType.toLowerCase()
          acc[translationType] = (acc[translationType] || 0) + 1
          return acc
        }, {} as Record<string, number>)

        const passengerDescriptions = Object.keys(passengerCounts)
        if (passengerDescriptions.length === 1) {
          const description = passengerDescriptions[0]
          const count = passengerCounts[description]
          return i18n.t('common.passenger.singlePassenger', {
            count,
            type: i18n.t(`common.passenger.${description}`)
          })
        } else {
          const totalPassengers = passengers.value.length
          return i18n.t('common.passenger.multiplePassengers', {
            count: totalPassengers
          })
        }
      })

      const formatPriceWithCurrency = (price: number) => {
        return `${formatPrice(price)} kr`
      }

      const openModal = () => {
        initialPassengers.value = JSON.parse(
          JSON.stringify(journeyStore.passengers)
        )
        isModalOpen.value = true
        document.body.style.overflow = 'hidden'
      }

      const closeModal = () => {
        isModalOpen.value = false
        document.body.style.overflow = 'auto'
      }

      const cancelChanges = () => {
        journeyStore.setPassengers(initialPassengers.value)
        closeModal()
      }

      return {
        isModalOpen,
        passengerText,
        openModal,
        closeModal,
        cancelChanges,
        passengers,
        person,
        formatPriceWithCurrency,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .edit-passenger__button {
    display: flex;
    flex-direction: row;
    align-items: center;
    color: $clr-text;
    padding: 0.7rem 1rem;
    border: 1px solid $clr-primary-50;
    border-radius: $border-radius;
    background-color: $clr-background;
    box-shadow: $box-shadow-card;
    cursor: pointer;
    width: 100%;

    svg {
      width: 15px;
      height: 15px;
      color: $clr-secondary;
      margin-right: 0.5rem;
    }

    &-text {
      @include body-large;
      display: flex;
      flex-direction: column;
      text-align: left;

      span {
        @include body-small;
      }
    }
  }

  .edit-passenger__modal-overlay {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    height: 100dvh;
    background-color: rgba(42, 52, 122, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .edit-passenger__modal-content {
    background-color: $clr-white;
    padding: 1rem;
    border-radius: $border-radius;
    box-shadow: $box-shadow-card;
    max-width: 606px;
    width: 100%;
    max-height: 90dvh;
    overflow-y: auto;
    margin-right: 16px;
    margin-left: 16px;

    @media (min-width: $breakpoint-xl) {
    }
  }

  .edit-passenger__modal-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 1rem;
  }

  .edit-passenger__modal-actions button {
    cursor: pointer;
    flex: 1;

    &:first-child {
      margin-right: 1rem;
    }
  }
</style>
